import React, { useEffect, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import { ListVATRequest } from "../../../../../services/soap/global/requests/ListVATRequest";

interface ItemVat {
  id: string;
  name: string;
  value: string;
}

interface IItemVatDropDownProps {
  selectedVat: string;
  onChange: (data: ItemVat | null) => void; // ✅ allow null on clear
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
}

const ItemVatDropDown: React.FC<IItemVatDropDownProps> = ({
  selectedVat,
  onChange,
  isRequired,
  errorMessage,
  validationState,
}) => {
  const { store, globalService } = useDependency();
  const { t } = useTranslation("products");
  const _t = (key: string) => t(`addNewProduct.${key}`, { ns: "products" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const isUserEditingRef = useRef(false);
  const didInitialAutoSelectRef = useRef(false);

  const list = useAsyncList<ItemVat>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 50;

      try {
        const request = new ListVATRequest(store.Server, store.SessionId, limit, offset);
        const response = await globalService.listVAT(request);

        let items: ItemVat[] = response?.ROWS?.map((x: any) => x.ITEM) || [];
        if (!Array.isArray(items)) return { items: [] };

        // ✅ search by VAT name
        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((v) => (v.name ?? "").toLowerCase().includes(ft));
        }

        return {
          items,
          cursor: items.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching VAT list:", error);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial load: auto-select 0th item ONCE
  useEffect(() => {
    if (
      !didInitialAutoSelectRef.current &&
      list.items.length > 0 &&
      !selectedVat // only if parent didn't already set something
    ) {
      didInitialAutoSelectRef.current = true;

      const first = list.items[0];
      setSelectedKey(String(first.id));
      setInputValue(first.name ?? "");
      onChange(first);
    }
  }, [list.items, selectedVat, onChange]);

  // ✅ sync from parent ONLY when user not typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedVat) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedVat));
    const selected = list.items.find((x) => String(x.id) === String(selectedVat));
    setInputValue(selected?.name ?? "");
  }, [selectedVat, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // ✅ allow deleting text without reinserting selection
    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // ✅ cleared => empty dropdown
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText(""); // next focus shows all
    }
  };

  return (
    <ComboBox
      label={_t("vat")}
      aria-label={_t("vat")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      width={{ base: "100%", L: "50%" }}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText(""); // ✅ show all when user clicks
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default ItemVatDropDown;
